import { JSX, ReactNode } from 'react';
import { FieldPath, UseFormReturn, useFormContext } from 'react-hook-form';

import { Trash2Icon } from 'lucide-react';

import { Button } from 'components/Button';
import { Label } from 'components/Label';
import { ReferenceTypeOption } from 'components/ReferenceCandidates/types';
import { appendReferenceValue } from 'components/ReferenceCandidates/utils';
import { Select } from 'components/Select';
import { Input } from 'components/input/Input';
import { MultiSelectPillInput } from 'components/input/MultiSelectPillInput';
import { Flex } from 'components/layout';
import { Typography } from 'components/typography';
import { NormalizedLoad } from 'types/Load';

import { PLS_FIELD_LIMITS } from './constants';
import {
  PendingReference,
  PlsFormValues,
  PlsReferenceFieldDefinition,
} from './types';
import { omitPlsReferenceField, toSelectStringValue } from './utils';

export enum PlsReferenceFieldScope {
  Load = 'plsLoadReferenceFields',
  Pickup = 'plsPickupReferenceFields',
  Consignee = 'plsConsigneeReferenceFields',
}

export type { PendingReference };

// Appends a card without a type. Ids come from the cards already there, not a counter, since a
// restored draft brings its cards' ids back after a reload.
export function withNewPendingReference(
  pending: PendingReference[],
  value = ''
): PendingReference[] {
  const id = Math.max(0, ...pending.map((reference) => reference.id)) + 1;
  return [...pending, { id, value }];
}

type ReferenceFieldsProps = {
  definitions: PlsReferenceFieldDefinition[];
  scope: PlsReferenceFieldScope;
  isLoading: boolean;
  error?: unknown;
  // Unused numbers from the email, offered in an empty card's value.
  candidateNumbers?: string[];
  // Cards waiting for their type. "+ Add Reference" and an untyped number from the email open one.
  pendingReferences: PendingReference[];
  onPendingReferencesChange: (pending: PendingReference[]) => void;
  // Types a card can pick that live outside this field map (the bill-to's customer reference
  // fields); picking one files the value through onAddOtherReference.
  otherReferenceTypes?: ReferenceTypeOption[];
  onAddOtherReference?: (code: string, value: string) => void;
};

// PLS reference fields as cards, the way other TMS forms show references: the type first, since
// reps look for "a delivery number", then the value.
export function ReferenceFields({
  definitions,
  scope,
  isLoading,
  error,
  candidateNumbers = [],
  pendingReferences,
  onPendingReferencesChange,
  otherReferenceTypes = [],
  onAddOtherReference,
}: ReferenceFieldsProps): JSX.Element {
  const {
    control,
    getValues,
    setValue,
    watch,
    formState: { errors },
  } = useFormContext<PlsFormValues>();
  // MultiSelectPillInput is typed to NormalizedLoad; PlsFormValues extends it.
  const normalizedControl =
    control as unknown as UseFormReturn<NormalizedLoad>['control'];
  const values = watch(scope) ?? {};
  const selectedCodes = Object.keys(values);
  const definitionsByCode = new Map(
    definitions.map((definition) => [definition.code, definition])
  );
  const typeOptions = definitions
    .filter(
      (definition, index, all) =>
        !!definition.code?.trim() &&
        all.findIndex((other) => other.code === definition.code) === index
    )
    .map((definition) => ({ value: definition.code, label: definition.name }));
  const unusedTypeOptions = typeOptions.filter(
    (option) => !selectedCodes.includes(option.value)
  );
  const otherCodes = new Set(otherReferenceTypes.map((type) => type.value));

  if (isLoading) {
    return (
      <Typography variant='body-sm' className='text-neutral-600'>
        Loading reference fields...
      </Typography>
    );
  }

  if (error) {
    return (
      <Typography variant='body-sm' className='text-error-600'>
        Reference fields could not be loaded. Please retry.
      </Typography>
    );
  }

  const fieldPath = (code: string) =>
    `${scope}.${code}` as FieldPath<PlsFormValues>;

  const removeField = (code: string) => {
    const updates = omitPlsReferenceField(getValues(), scope, code);
    (
      Object.entries(updates) as [
        keyof PlsFormValues,
        PlsFormValues[keyof PlsFormValues],
      ][]
    ).forEach(([field, value]) => {
      setValue(field, value, { shouldDirty: true });
    });
  };

  // Files a value under a type: a field map type gets it added to its list, any other type goes
  // to onAddOtherReference.
  const fileValue = (code: string, value: string) => {
    if (otherCodes.has(code)) {
      onAddOtherReference?.(code, value.trim());
      return;
    }
    const current = getValues(fieldPath(code));
    const currentValue = typeof current === 'string' ? current : '';
    setValue(
      fieldPath(code),
      value.trim() ? appendReferenceValue(currentValue, value) : currentValue,
      { shouldDirty: true }
    );
  };

  const changeFieldType = (fromCode: string, toCode: string) => {
    if (!toCode || toCode === fromCode) {
      return;
    }
    const value = String(getValues(fieldPath(fromCode)) ?? '');
    removeField(fromCode);
    value
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean)
      .forEach((item) => fileValue(toCode, item));
    if (!value.trim()) {
      fileValue(toCode, '');
    }
  };

  const updatePending = (id: number, update: Partial<PendingReference>) =>
    onPendingReferencesChange(
      pendingReferences.map((pending) =>
        pending.id === id ? { ...pending, ...update } : pending
      )
    );
  const removePending = (id: number) =>
    onPendingReferencesChange(
      pendingReferences.filter((pending) => pending.id !== id)
    );

  const hasCards = selectedCodes.length > 0 || pendingReferences.length > 0;

  return (
    <Flex direction='col' gap='md' className='w-full'>
      {!hasCards && (
        <Typography variant='body-sm' className='text-neutral-600'>
          No references yet.
        </Typography>
      )}

      {selectedCodes.map((code, index) => {
        const label = definitionsByCode.get(code)?.name ?? code;
        const name = fieldPath(code) as FieldPath<NormalizedLoad>;
        // Only a card still waiting for its value offers them, so filled cards stay quiet.
        const fieldCandidates = values[code]?.trim() ? [] : candidateNumbers;

        return (
          <ReferenceCard
            key={code}
            title={`Reference #${index + 1}`}
            removeLabel={`Remove ${label}`}
            onRemove={() => removeField(code)}
          >
            <div>
              <Label name={`${scope}Type.${code}`} required hideAIHint>
                Type
              </Label>
              <Select
                showSearch
                className='h-8 w-full'
                name={`${scope}Type.${code}`}
                value={code}
                optionFilterProp='label'
                options={[
                  ...(definitionsByCode.has(code)
                    ? []
                    : [{ value: code, label }]),
                  ...typeOptions.filter(
                    (option) =>
                      option.value === code ||
                      !selectedCodes.includes(option.value)
                  ),
                ]}
                onChange={(value) =>
                  changeFieldType(code, toSelectStringValue(value))
                }
              />
            </div>
            <MultiSelectPillInput
              name={name}
              label='Value'
              control={normalizedControl}
              optionValues={fieldCandidates}
              showSuggestions={fieldCandidates.length > 0}
              autoSelectFirstOption={false}
              maxChars={PLS_FIELD_LIMITS.referenceFieldValue}
              rules={{
                maxLength: {
                  value: PLS_FIELD_LIMITS.referenceFieldValue,
                  message: `Must be ${PLS_FIELD_LIMITS.referenceFieldValue} characters or fewer`,
                },
              }}
            />
          </ReferenceCard>
        );
      })}

      {pendingReferences.map((pending, index) => (
        <ReferenceCard
          key={pending.id}
          title={`Reference #${selectedCodes.length + index + 1}`}
          removeLabel='Remove new reference'
          onRemove={() => removePending(pending.id)}
        >
          <div>
            <Label
              name={`${scope}PendingType.${pending.id}`}
              required
              hideAIHint
            >
              Type
            </Label>
            <Select
              showSearch
              className='h-8 w-full'
              name={`${scope}PendingType.${pending.id}`}
              value=''
              placeholder='Select reference type'
              optionFilterProp='label'
              // Load reference types stay listed after a card exists, because a second number
              // appends to that card. A customer reference holds one value and would replace
              // it, so those types are offered once the card has a value to file.
              options={[
                ...typeOptions,
                ...(pending.value.trim() ? otherReferenceTypes : []),
              ]}
              onChange={(value) => {
                const code = toSelectStringValue(value);
                if (!code) {
                  return;
                }
                fileValue(code, pending.value);
                removePending(pending.id);
              }}
            />
          </div>
          <div>
            <Label name={`plsPendingReferences.${index}.value`} hideAIHint>
              Value
            </Label>
            <Input
              name={`plsPendingReferences.${index}.value`}
              className='mt-1 w-full'
              aria-label='Reference value'
              placeholder='Enter value'
              value={pending.value}
              maxLength={PLS_FIELD_LIMITS.referenceFieldValue}
              onChange={(event) =>
                updatePending(pending.id, { value: event.target.value })
              }
            />
            {!pending.value.trim() && candidateNumbers.length > 0 && (
              <Flex wrap='wrap' gap='sm' className='mt-2'>
                {candidateNumbers.map((number) => (
                  <Button
                    key={number}
                    type='button'
                    variant='unstyled'
                    size='auto'
                    className='rounded-md border border-neutral-400 bg-neutral-100 px-2 py-1 text-xs text-neutral-700 hover:bg-neutral-200'
                    onClick={() => updatePending(pending.id, { value: number })}
                    buttonNamePosthog={null}
                  >
                    {number}
                  </Button>
                ))}
              </Flex>
            )}
            {errors.plsPendingReferences?.[index]?.value?.message && (
              <Typography variant='body-xs' className='mt-1 text-error-500'>
                {errors.plsPendingReferences[index].value.message}
              </Typography>
            )}
          </div>
        </ReferenceCard>
      ))}

      <Flex align='center' justify='end' className='w-full'>
        <Button
          type='button'
          variant='ghost'
          size='sm'
          className='h-8 px-3 text-xs text-brand-500'
          disabled={unusedTypeOptions.length + otherReferenceTypes.length === 0}
          onClick={() =>
            onPendingReferencesChange(
              withNewPendingReference(pendingReferences)
            )
          }
          buttonNamePosthog={null}
        >
          + Add Reference
        </Button>
      </Flex>
    </Flex>
  );
}

function ReferenceCard({
  title,
  removeLabel,
  onRemove,
  children,
}: {
  title: string;
  removeLabel: string;
  onRemove: () => void;
  children: ReactNode;
}): JSX.Element {
  return (
    <div
      className='w-full rounded border border-neutral-200 p-3'
      data-testid='pls-reference-card'
    >
      <Flex align='center' justify='between' className='mb-3'>
        <Typography variant='body-sm' weight='semibold'>
          {title}
        </Typography>
        <Button
          type='button'
          variant='ghost'
          size='xs'
          aria-label={removeLabel}
          onClick={onRemove}
          buttonNamePosthog={null}
        >
          <Trash2Icon className='h-4 w-4' />
        </Button>
      </Flex>
      <Flex direction='col' align='stretch' gap='lg' className='w-full'>
        {children}
      </Flex>
    </div>
  );
}
